import { BookOpen } from 'lucide-react';
import { m } from 'motion/react';
import type { ReactNode } from 'react';

import { AbsorStaffLockup } from '../../components/Brand';
import { LanguagePicker, ThemeToggle } from '../../components/Switchers';
import { useT } from '../../i18n/provider';

/**
 * El marco que comparten las pantallas públicas de `13 §2`.
 *
 * Es la primera pantalla del producto y hasta ahora era una caja blanca con un
 * título: no decía de quién era. Ahora lleva la marca, y a su derecha un panel
 * que solo existe a partir de `lg`, donde el degradado de Absor tiene sitio sin
 * robárselo al formulario.
 *
 * El selector de idioma está aquí, antes de haber iniciado sesión, porque es
 * justo donde hace falta: sin sesión no hay `users.locale` que leer, y quien
 * llega con el navegador en otro idioma tiene que poder cambiarlo sin entrar.
 */
export function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const t = useT();

  return (
    <div className="grid min-h-screen bg-canvas lg:grid-cols-[1fr_minmax(0,42rem)]">
      <div className="relative flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="absolute top-5 right-5 flex items-center gap-1">
          <LanguagePicker />
          <ThemeToggle />
        </div>

        <m.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 0.8, 0.28, 1] }}
          className="mx-auto w-full max-w-sm space-y-7"
        >
          <AbsorStaffLockup className="text-lg text-ink lg:hidden" />

          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle ? <p className="mt-2 text-sm text-ink-soft">{subtitle}</p> : null}
          </div>

          {children}

          <nav aria-label={t('auth.documentation.title')} className="space-y-2 border-t border-line pt-6 text-sm">
            <a
              href="/docs/feedback/"
              className="inline-flex items-center gap-2 font-semibold text-primary transition-colors hover:text-primary-dark"
            >
              <BookOpen aria-hidden="true" className="size-4 shrink-0" />
              <span>{t('auth.documentation.title')}</span>
            </a>
            <p className="text-ink-soft">{t('auth.documentation.hint')}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <a
                href="/docs/feedback/index.md"
                type="text/markdown"
                className="text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors hover:text-ink"
              >
                {t('auth.documentation.markdown')}
              </a>
              <a
                href="/docs/feedback/openapi.json"
                type="application/json"
                className="text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors hover:text-ink"
              >
                {t('auth.documentation.openapi')}
              </a>
            </div>
          </nav>
        </m.div>
      </div>

      {/*
        El panel de marca es decorativo y se esconde del árbol de accesibilidad.
        Los enlaces a la documentación están junto al formulario, también en móvil.
      */}
      <aside aria-hidden="true" className="relative hidden overflow-hidden bg-nav lg:block">
        <div className="brand-gradient absolute -top-40 -right-32 size-[38rem] rounded-full opacity-35 blur-3xl" />
        <div className="brand-gradient absolute -bottom-52 -left-24 size-[30rem] rounded-full opacity-20 blur-3xl" />
        <div className="canvas-dots absolute inset-0 text-white/8" />

        <div className="relative flex h-full flex-col justify-between p-14">
          <AbsorStaffLockup className="self-start text-[1.75rem] text-white" />

          <p className="font-mono text-xs text-white/35">absor.top</p>
        </div>
      </aside>
    </div>
  );
}
